import db from "../../database";
import Axios from "axios";
import useAuthStore from "../../stores/useAuthStore";
import { resolveGiftCardPrintType, getTillTemplateXml, describeTillTemplates, describePrintLine, templateFormat, safeTrace } from "./giftCardPrintType";
import { notifyTemplateNotConfigured, PRINT_TEMPLATES } from "./printTemplateErrors";

const printGiftCardCW = async (printData, requestedType) => {
  const tillData = useAuthStore.getState().tillData;
  const printerURL = tillData.tillAccess.cwrTill.hardwareController.imageUrl;
  let giftCardTemplate;
  let templateSource = "none";
  let legacyPick = null;

  safeTrace("CW print start", { requestedType, line: describePrintLine(printData), tillTemplatesHeldByApp: describeTillTemplates(tillData?.tillAccess?.cwrTill) });

  // TSK-4186: till-level Gift Card / Credit Note / Voucher template, once configured in back office
  // (cwr_till.cwr_giftcard_print_template_id / _creditnote_ / _voucher_). A Voucher / Credit Note-group card is resolved
  // from its gift card type's group so it no longer prints with the Gift Card template.
  const type = await resolveGiftCardPrintType(printData, requestedType);
  const tillTemplateXml = getTillTemplateXml(tillData?.tillAccess?.cwrTill, type);

  if (tillTemplateXml) {
    try {
      giftCardTemplate = JSON.parse(tillTemplateXml);
      templateSource = "till-level";
    } catch (error) {
      // e.g. an XML template assigned to a CW (JSON) till -- log it and fall back instead of printing nothing
      console.error("[GiftCardPrint] till template is not valid JSON for the CW controller, using the legacy template", error);
      safeTrace("CW till template unusable, falling back to legacy", { type, format: templateFormat(tillTemplateXml), error: String(error?.message || error) });
    }
  }
  if (!giftCardTemplate) {
    // Till-level template not configured (or unusable) -- fall back to the legacy
    // shared "DIG" giftCardData template so existing tills keep printing.
    let giftCardData = await db.giftCardData?.toArray();
    giftCardData?.map((GCData) => {
      if (GCData?.type === "DIG") {
        giftCardTemplate = GCData?.printTemplate?.xmlcode2 ? JSON.parse(GCData?.printTemplate?.xmlcode2) : {};
        legacyPick = { typeId: GCData?.cwrGiftcardTypeId, typeName: GCData?.name, group: GCData?.giftCardGroup, template: GCData?.printTemplate?.name || null };
      }
    });
    templateSource = "legacy-shared-DIG";
  }
  safeTrace("CW template chosen", {
    requestedType,
    resolvedType: type,
    source: templateSource,
    tillTemplateFound: !!tillTemplateXml,
    tillTemplateFormat: templateFormat(tillTemplateXml),
    legacyPick,
    elementCount: giftCardTemplate?.elements?.length ?? 0,
    printerURL,
  });
  if (!Array.isArray(giftCardTemplate?.elements)) {
    notifyTemplateNotConfigured(PRINT_TEMPLATES.giftCard, type);
    return;
  }
  // Prepare the data object with all necessary sources
  const data = {
    tillData: tillData,
    printData: printData || {}, // Assuming printData is a single object
    type: type, // Pass type for conditional logic
  };

  // Process the JSON template with dynamic data
  const processedTemplate = processReceiptTemplate(giftCardTemplate, data);
  let GiftCardHCPrintPayload = JSON.stringify(processedTemplate, null, 2);
  Axios.post(`${printerURL}printer`, GiftCardHCPrintPayload, {
    headers: {
      "Content-Type": "text/plain",
    },
  })
    .then((res) => {
      safeTrace("CW print request sent", { resolvedType: type, status: res?.status ?? null, firstText: processedTemplate?.elements?.find((e) => e?.type === "text")?.content || null });
    })
    .catch((error) => {
      safeTrace("CW print request FAILED", { resolvedType: type, printerURL, error: String(error?.message || error), status: error?.response?.status ?? null });
    });
  return processedTemplate; // Return the processed JSON
};

// Traced wrapper: a failure is recorded and re-thrown exactly as before (callers do not await this).
const GiftCardCWHCPrint = async (printData, requestedType) => {
  try {
    return await printGiftCardCW(printData, requestedType);
  } catch (error) {
    safeTrace("CW print threw", { requestedType, line: describePrintLine(printData), error: String(error?.message || error) });
    throw error;
  }
};

// JSON processing function
const processReceiptTemplate = (template, data) => {
  const processedTemplate = JSON.parse(JSON.stringify(template));
  processedTemplate.elements = processedTemplate.elements.flatMap((element) => {
    // barcode/qrcode content must be resolved too, otherwise the raw "{{printData.cardNo}}" key is encoded and printed under the code
    if (["text", "barcode", "qrcode"].includes(element.type) && element.content && typeof element.content === "string") {
      element.content = replacePlaceholders(element.content, data);
    }
    return element;
  });
  return processedTemplate;
};

// Placeholder replacement with expression evaluation
const replacePlaceholders = (text, data) => {
  return text.replace(/{{([^}]+)}}/g, (match, placeholder) => {
    if (placeholder === "currentDateTime") return new Date().toLocaleString();
    if (placeholder.includes("?")) {
      try {
        const [condition, trueVal, falseVal] = placeholder.split(/ *\? *| *: */);
        const conditionResult = evalCondition(condition, data);
        return conditionResult ? trueVal.trim() : falseVal.trim();
      } catch (error) {
        console.error("Error evaluating conditional expression:", placeholder, error);
        return "";
      }
    }
    const value = getDeepValue(data, placeholder);
    return value !== null && value !== undefined ? (typeof value === "number" ? value.toString() : value) : "";
  });
};

const evalCondition = (condition, data) => {
  try {
    const [left, operator, right] = condition.split(/ *(===|!==|>|<|>=|<=) */);
    const leftVal = getDeepValue(data, left.trim()) ?? left.trim().replace(/['"]/g, "");
    const rightVal = getDeepValue(data, right.trim()) ?? right.trim().replace(/['"]/g, "");
    switch (operator) {
      case "===":
        return leftVal === rightVal;
      case "!==":
        return leftVal !== rightVal;
      case ">":
        return Number(leftVal) > Number(rightVal);
      case "<":
        return Number(leftVal) < Number(rightVal);
      case ">=":
        return Number(leftVal) >= Number(rightVal);
      case "<=":
        return Number(leftVal) <= Number(rightVal);
      default:
        return false;
    }
  } catch (error) {
    console.error("Error evaluating condition:", condition, error);
    return false;
  }
};

// Deep value retrieval
const getDeepValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), obj);
};

export default GiftCardCWHCPrint;
